/**
 * Phase 105-Agent-B — MultiParcelChecklist.
 *
 * Renders one row per parcel in `order.items[]` so the agent can tick off
 * each physical package they have in hand before "Capture Pickup Proof"
 * is enabled. Single-parcel orders auto-confirm via the backend (empty
 * verified list → all_verified=true server-side).
 *
 * On submit, POSTs to `/api/orders/{tid}/parcels/verify` and bubbles up
 * the `all_verified` flag so the parent stage advancer can move on.
 *
 * Discrepancies (missing / damaged / weight_mismatch / dim_mismatch /
 * other) live in a side block per parcel — agent picks the type from a
 * small dropdown, types an optional note, and submission still goes
 * through (the backend records the discrepancy so dispatch can react).
 */
import { useMemo, useState } from "react";
import { CheckSquare, Square, Camera, AlertTriangle, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";

const DISCREPANCY_TYPES = [
  { key: "missing",          label: "Missing parcel" },
  { key: "damaged",          label: "Damaged" },
  { key: "weight_mismatch",  label: "Weight mismatch" },
  { key: "dim_mismatch",     label: "Dim mismatch" },
  { key: "other",            label: "Other" },
];

function parcelId(item, idx) {
  return item?.parcel_id || item?.id || `parcel-${idx + 1}`;
}

export default function MultiParcelChecklist({ order, onVerified, busyParent = false }) {
  const items = Array.isArray(order?.items) ? order.items : [];
  const total = items.length;

  // Single-parcel orders → render a compact confirm card; submit goes
  // through with an empty verified list (single-parcel auto-verifies).
  const singleParcel = total <= 1;

  const [ticked, setTicked] = useState(() => new Set());
  // discrepancies keyed by parcel_id
  const [discrepancies, setDiscrepancies] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const toggle = (pid) => {
    setTicked((curr) => {
      const next = new Set(curr);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  const addDiscrepancy = (pid) => {
    setDiscrepancies((curr) => ({
      ...curr,
      [pid]: curr[pid] ? null : { parcel_id: pid, type: "damaged", note: "" },
    }));
  };
  const updDiscrepancy = (pid, patch) => {
    setDiscrepancies((curr) => ({ ...curr, [pid]: { ...curr[pid], ...patch } }));
  };

  const allTicked = useMemo(() => {
    if (singleParcel) return true;
    return items.every((it, i) => ticked.has(parcelId(it, i)));
  }, [singleParcel, items, ticked]);

  const submitBlockedReason = singleParcel
    ? null
    : !allTicked
      ? `Tick each parcel — ${ticked.size}/${total} confirmed`
      : null;

  const submit = async () => {
    setSubmitting(true);
    try {
      const verified_parcel_ids = singleParcel ? [] : Array.from(ticked);
      const discList = Object.values(discrepancies).filter(Boolean);
      const { data } = await api.post(`/orders/${order.tracking_id}/parcels/verify`, {
        verified_parcel_ids,
        discrepancies: discList,
      });
      const allVerified = !!data?.summary?.all_verified;
      if (allVerified) {
        toast.success(singleParcel ? "Parcel confirmed." : `All ${total} parcels confirmed.`);
      } else {
        toast.warning(`Verified with ${discList.length} discrepancy${discList.length === 1 ? "" : "ies"} — dispatch notified.`);
      }
      if (onVerified) onVerified({ all_verified: allVerified, summary: data?.summary });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't verify parcels";
      toast.error(typeof detail === "string" ? detail : "Couldn't verify parcels");
    } finally {
      setSubmitting(false);
    }
  };

  // Single-parcel compact card
  if (singleParcel) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="multi-parcel-single">
        <div className="flex items-center gap-2 text-emerald-700">
          <CheckSquare className="h-4 w-4" />
          <span className="text-sm font-semibold">Single-parcel order — auto-confirmed</span>
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || busyParent}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-60"
          data-testid="multi-parcel-submit-btn"
        >
          {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Confirming…</> : "Confirm parcel"}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="multi-parcel-checklist">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Verify parcels</p>
          <p className="text-sm font-semibold text-slate-900">
            Tick each parcel in your possession ({ticked.size}/{total})
          </p>
        </div>
      </div>

      <ul className="space-y-2" data-testid="multi-parcel-list">
        {items.map((it, i) => {
          const pid = parcelId(it, i);
          const on = ticked.has(pid);
          const photoSrc = it.package_photo_url || it.customer_package_photo;
          const disc = discrepancies[pid];
          return (
            <li
              key={pid}
              className={`flex items-start gap-3 rounded-xl border-2 p-3 transition ${
                on ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200"
              }`}
              data-testid={`parcel-row-${pid}`}
            >
              <button
                type="button"
                onClick={() => toggle(pid)}
                className="shrink-0 mt-0.5"
                aria-label={on ? "Untick parcel" : "Tick parcel"}
                data-testid={`parcel-tick-${pid}`}
              >
                {on
                  ? <CheckSquare className="h-5 w-5 text-emerald-600" />
                  : <Square className="h-5 w-5 text-slate-400" />}
              </button>
              {photoSrc ? (
                <img
                  src={photoSrc}
                  alt={it.item_name || `Parcel ${i + 1}`}
                  className="w-12 h-12 rounded-lg object-cover border border-slate-200 shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center">
                  <Camera className="h-4 w-4 text-slate-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {it.item_name || `Parcel ${i + 1}`}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {it.weight_kg ?? it.weight ?? "?"} kg
                  {(it.length_cm || it.width_cm || it.height_cm)
                    ? ` · ${it.length_cm || "?"}×${it.width_cm || "?"}×${it.height_cm || "?"} cm`
                    : ""}
                </p>
                <button
                  type="button"
                  onClick={() => addDiscrepancy(pid)}
                  className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold ${
                    disc ? "text-rose-600" : "text-slate-500 hover:text-rose-600"
                  }`}
                  data-testid={`parcel-discrepancy-toggle-${pid}`}
                >
                  <AlertTriangle className="h-3 w-3" /> {disc ? "Remove issue" : "Report an issue"}
                </button>
                {disc && (
                  <div className="mt-2 space-y-1.5" data-testid={`parcel-discrepancy-${pid}`}>
                    <select
                      value={disc.type}
                      onChange={(e) => updDiscrepancy(pid, { type: e.target.value })}
                      className="w-full text-xs rounded-md border border-slate-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-rose-300"
                    >
                      {DISCREPANCY_TYPES.map((d) => (
                        <option key={d.key} value={d.key}>{d.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={disc.note || ""}
                      onChange={(e) => updDiscrepancy(pid, { note: e.target.value })}
                      placeholder="Note (optional)"
                      className="w-full text-xs rounded-md border border-slate-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-rose-300"
                    />
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {submitBlockedReason && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 flex items-center gap-1.5" data-testid="multi-parcel-blocked-reason">
          <ShieldAlert className="h-3.5 w-3.5" /> Almost there — {submitBlockedReason}
        </p>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={!!submitBlockedReason || submitting || busyParent}
        title={submitBlockedReason || undefined}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
        data-testid="multi-parcel-submit-btn"
      >
        {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Confirming…</> : `Confirm ${total} parcels`}
      </button>
    </div>
  );
}
